/* PhenotypeClassifier — PAM50 (рак на гърдата) и PCOS Rotterdam subtypes UI
 *
 * Таб 1 "Рак на гърдата (PAM50)": ER / PR / HER2 / Ki-67 → класификация
 *     → resultat card с emphasise / avoid / citation + "Запиши в профила"
 * Таб 2 "PCOS (Rotterdam)": androgen excess / ov. dysfunction / polycystic
 *     ovaries → 1 от 4 фенотипа A-D
 *
 * Backend API: /api/phenotype-subtype/classify, /assign, /my
 *
 * При успешен assign — добавя човешки-четим tag в profile.conditions
 * (напр. "Рак на гърдата TNBC (PAM50)") + записва code в
 * profile.phenotype_subtypes → AI препоръките вече виждат фенотипа.
 */
import React, { useState, useEffect, useCallback, createContext, useContext, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dna, Activity, CheckCircle2, XCircle, Trash2, FlaskConical, ChevronDown, ChevronUp, AlertTriangle, ExternalLink, Droplet, Leaf, HeartPulse, Flower2, Sparkles, Brain, Zap, Bone, Wind, Ribbon, Radiation, Pill } from "lucide-react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import CollapsiblePanel from "./CollapsiblePanel";
import ErrorBoundary from "./ErrorBoundary";
import { IndexInfoButton } from "./IndexInfoModal";
import HydroxyprolineProtocolCard from "./research/HydroxyprolineProtocolCard";

// iter321 — Context so doctor-side users can act on behalf of a patient
// without touching every nested component's API call signature.
const PhenotypeTargetContext = createContext({ targetUserId: null });
const useTargetUserId = () => useContext(PhenotypeTargetContext).targetUserId;
const withTargetBody = (body, targetUserId) =>
  targetUserId ? { ...body, target_user_id: targetUserId } : body;
const withTargetQuery = (url, targetUserId) =>
  targetUserId ? `${url}${url.includes("?") ? "&" : "?"}target_user_id=${encodeURIComponent(targetUserId)}` : url;

const CONDITIONS = [
  // ─── ОНКОЛОГИЯ — всички карциноми обединени ───
  { id: "oncology",   label_bg: "Онкология",          label_en: "Oncology",          label_es: "Oncología",          icon: Ribbon },
  // ─── ГИНЕКОЛОГИЯ — една обединена секция (PCOS + ендометриоза + миома + полип + аденомиоза) ───
  { id: "gynecology", label_bg: "Гинекология",        label_en: "Gynecology",        label_es: "Ginecología",        icon: Flower2 },
  // ─── МАМОЛОГИЯ — ребрандиран breast_benign + IGM ───
  { id: "mammology",  label_bg: "Мамология",           label_en: "Mammology",         label_es: "Mamología",          icon: Sparkles },
  // ─── ЕНДОКРИНОЛОГИЯ — обединява щитовидна + редки ендокринни ───
  { id: "endocrinology", label_bg: "Ендокринология",   label_en: "Endocrinology",     label_es: "Endocrinología",    icon: Activity },
  // ─── МЕТАБОЛИТНИ ───
  { id: "t2dm",       label_bg: "Диабет T2DM",         label_en: "T2DM",              label_es: "DM2",                icon: Activity },
  { id: "mafld",      label_bg: "MAFLD",               label_en: "MAFLD",             label_es: "MAFLD",              icon: Droplet },
  // ─── СЪРДЕЧНО-СЪДОВИ ───
  { id: "cardiovascular", label_bg: "Сърдечно-съдови", label_en: "Cardiovascular",    label_es: "Cardiovascular",    icon: HeartPulse },
  // ─── ГАСТРО + БЪБРЕЦИ ───
  { id: "ibd",        label_bg: "IBD",                 label_en: "IBD",               label_es: "EII",                icon: Leaf },
  { id: "renal",      label_bg: "Бъбречни заболявания", label_en: "Renal disease",    label_es: "Enfermedad renal",  icon: Droplet },
  // ─── НЕВРОЛОГИЯ + ПСИХ ───
  { id: "depression", label_bg: "Депресия",            label_en: "Depression",        label_es: "Depresión",          icon: Brain },
  { id: "ms",         label_bg: "Мултиплена склероза", label_en: "Multiple Sclerosis", label_es: "Esclerosis Múltiple", icon: Zap },
  { id: "migraine",   label_bg: "Мигрена",             label_en: "Migraine",          label_es: "Migraña",            icon: Brain },
  // ─── РЕВМАТОЛОГИЯ + ДЕНТАЛНО ───
  { id: "rheumatoid_arthritis", label_bg: "Ревматоиден артрит", label_en: "Rheumatoid Arthritis", label_es: "Artritis Reumatoide", icon: Bone },
  { id: "dental",     label_bg: "Дентални заболявания", label_en: "Dental diseases",  label_es: "Enfermedades dentales", icon: Bone },
];

// ───────────────────────── PAM50 form ─────────────────────────
function BreastCancerForm({ API, onAssigned }) {
  const [markers, setMarkers] = useState({ ER: null, PR: null, HER2: null, Ki67: null });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const setM = (k, v) => setMarkers((m) => ({ ...m, [k]: v }));

  const canClassify = markers.ER !== null && markers.HER2 !== null;

  const classify = async () => {
    if (!canClassify) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/phenotype-subtype/classify`, {
        condition: "breast_cancer",
        markers,
      });
      setResult(data);
      if (!data.code) toast.warning("Маркерите не са достатъчни за ясна класификация.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при класификацията");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="pam50-form">
      <p className="text-[11px] text-gray-500">
        Въведете ключовите имунохистохимични маркери от хистологичния ви доклад
        (BIRADS / патология). Класификацията не замества онколог.
      </p>

      <MarkerRow label="ER (естрогенен рецептор)" value={markers.ER} onChange={(v) => setM("ER", v)} testId="mk-er" />
      <MarkerRow label="PR (прогестеронен рецептор)" value={markers.PR} onChange={(v) => setM("PR", v)} testId="mk-pr" />
      <MarkerRow label="HER2" value={markers.HER2} onChange={(v) => setM("HER2", v)} testId="mk-her2" />

      <div className="flex items-center gap-2 text-[12px]">
        <span className="text-gray-500 w-32">Ki-67 индекс</span>
        <div className="flex gap-1.5">
          {["low", "high", "very_low"].map((opt) => (
            <button key={opt} onClick={() => setM("Ki67", opt)}
              className={`px-2.5 py-1 rounded-full text-[11px] transition-colors ${markers.Ki67 === opt ? "bg-sky-500 text-white" : "bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300"}`}
              data-testid={`mk-ki67-${opt}`}>
              {opt === "low" ? "нисък (<14%)" : opt === "high" ? "висок (≥14%)" : "много нисък"}
            </button>
          ))}
        </div>
      </div>

      <button onClick={classify} disabled={!canClassify || loading}
        className="w-full py-2 rounded-lg text-sm font-semibold bg-sky-500 hover:bg-sky-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        data-testid="pam50-classify-btn">
        {loading ? "Класифициране..." : "Класифицирай PAM50 subtype"}
      </button>

      {result && <SubtypeResultCard result={result} condition="breast_cancer" API={API} onAssigned={onAssigned} />}
    </div>
  );
}

// ───────────────────────── PCOS form ─────────────────────────
function PcosForm({ API, onAssigned }) {
  const [markers, setMarkers] = useState({ androgen_excess: null, ovulation_dysfunction: null, polycystic_ovaries: null });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const setM = (k, v) => setMarkers((m) => ({ ...m, [k]: v }));

  const present = Object.values(markers).filter((x) => x !== null).length;

  const classify = async () => {
    if (present < 3) {
      toast.error("Попълнете и трите Rotterdam критерия.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/phenotype-subtype/classify`, {
        condition: "pcos",
        markers,
      });
      setResult(data);
      if (!data.code)
        toast.warning("Не отговаря на критериите на Rotterdam (трябват поне 2/3 положителни).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при класификацията");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="pcos-form">
      <p className="text-[11px] text-gray-500">
        Трите Rotterdam 2003 критерия: хиперандрогенизъм (клиничен или лаб.),
        овулаторна дисфункция, поликистозна морфология на яйчниците (TVS).
        PCOS се диагностицира при ≥2 от 3 положителни.
      </p>

      <MarkerRow label="Хиперандрогенизъм" sub="(хирсутизъм, акне, или ↑T)" value={markers.androgen_excess} onChange={(v) => setM("androgen_excess", v)} testId="mk-androgen" />
      <MarkerRow label="Овулаторна дисфункция" sub="(олиго/аменорея)" value={markers.ovulation_dysfunction} onChange={(v) => setM("ovulation_dysfunction", v)} testId="mk-ovulation" />
      <MarkerRow label="Поликистозни яйчници" sub="(≥12 фоликула или ≥10 mL обем)" value={markers.polycystic_ovaries} onChange={(v) => setM("polycystic_ovaries", v)} testId="mk-polycystic" />

      <button onClick={classify} disabled={loading}
        className="w-full py-2 rounded-lg text-sm font-semibold bg-sky-500 hover:bg-sky-600 text-white disabled:opacity-40 transition-colors"
        data-testid="pcos-classify-btn">
        {loading ? "Класифициране..." : "Класифицирай Rotterdam phenotype"}
      </button>

      {result && <SubtypeResultCard result={result} condition="pcos" API={API} onAssigned={onAssigned} />}
    </div>
  );
}

// ───────────────────────── Thyroid form ─────────────────────────
function ThyroidForm({ API, onAssigned }) {
  const [markers, setMarkers] = useState({
    TSH: null, fT4: null, TPO_antibodies: null, TSI_antibodies: null, non_thyroidal_illness: null,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const setM = (k, v) => setMarkers((m) => ({ ...m, [k]: v }));

  const classify = async () => {
    if (markers.TSH === null) { toast.error("Въведете поне TSH стойност."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/phenotype-subtype/classify`, { condition: "thyroid", markers });
      setResult(data);
      if (!data.code) toast.warning("Недостатъчно данни за фенотипа.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при класификацията");
    } finally { setLoading(false); }
  };

  const chip = (k, v, label) => {
    const active = markers[k] === v;
    return (
      <button key={`${k}-${v}`} onClick={() => setM(k, active ? null : v)}
        data-testid={`th-${k}-${v}`}
        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
          active ? "bg-sky-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"
        }`}>
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-3" data-testid="thyroid-form">
      <p className="text-[11px] text-gray-500">
        Въведете вашите тиреоидни лабораторни маркери. Класификацията е
        образователна и не замества ендокринологична оценка.
      </p>

      <div className="space-y-2 text-[11px]">
        <div>
          <div className="font-semibold mb-1 text-gray-700 dark:text-gray-300">TSH (mIU/L)</div>
          <div className="flex flex-wrap gap-1.5">
            {chip("TSH", "low", "Ниско (<0.4)")}
            {chip("TSH", "normal", "Нормално (0.4-4)")}
            {chip("TSH", "mildly_elevated", "Леко ↑ (4-10)")}
            {chip("TSH", "high", "Високо (>10)")}
          </div>
        </div>
        <div>
          <div className="font-semibold mb-1 text-gray-700 dark:text-gray-300">fT4</div>
          <div className="flex flex-wrap gap-1.5">
            {chip("fT4", "low", "Нисък")}
            {chip("fT4", "normal", "Нормален")}
            {chip("fT4", "high", "Висок")}
          </div>
        </div>
        <div>
          <div className="font-semibold mb-1 text-gray-700 dark:text-gray-300">Автоантитела</div>
          <div className="flex flex-wrap gap-1.5">
            {chip("TPO_antibodies", true, "TPO+ (Hashimoto)")}
            {chip("TSI_antibodies", true, "TSI/TRAb+ (Graves)")}
            {chip("non_thyroidal_illness", true, "Критично болен (NTIS)")}
          </div>
        </div>
      </div>

      <button onClick={classify} disabled={loading}
        className="w-full py-2 rounded-lg text-sm font-semibold bg-sky-500 hover:bg-sky-600 text-white disabled:opacity-40 transition-colors"
        data-testid="thyroid-classify-btn">
        {loading ? "Класифициране..." : "Класифицирай тиреоиден фенотип"}
      </button>

      {result && <SubtypeResultCard result={result} condition="thyroid" API={API} onAssigned={onAssigned} />}
    </div>
  );
}

// ───────────────────────── Endometriosis form ─────────────────────────
function EndometriosisForm({ API, onAssigned }) {
  const [points, setPoints] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const classify = async () => {
    const pts = parseFloat(points);
    if (!Number.isFinite(pts) || pts < 1) { toast.error("Въведете rASRM точки (≥1)."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/phenotype-subtype/classify`, {
        condition: "endometriosis", markers: { rASRM_points: pts },
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при класификацията");
    } finally { setLoading(false); }
  };

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/endometriosis/${code}`);
      setResult({ condition: "endometriosis", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid="endometriosis-form">
      <p className="text-[11px] text-gray-500">
        Въведете броя rASRM точки от оперативния протокол (1-5=Стадий I,
        6-15=II, 16-40=III, &gt;40=IV) или изберете директно стадий.
      </p>

      <div className="flex items-center gap-2">
        <input type="number" inputMode="decimal" step="1" min="1" max="200"
          value={points} onChange={(e) => setPoints(e.target.value)}
          placeholder="rASRM точки (напр. 18)"
          data-testid="endo-rasrm-input"
          className="flex-1 px-3 py-1.5 text-[12px] rounded-md border border-gray-200 bg-white dark:bg-white/5 dark:border-white/10 dark:text-white" />
        <button onClick={classify} disabled={loading}
          className="px-3 py-1.5 rounded-md text-[11px] font-semibold bg-sky-500 hover:bg-sky-600 text-white disabled:opacity-40"
          data-testid="endo-classify-btn">
          {loading ? "..." : "Класифицирай"}
        </button>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
          Или директно изберете стадий:
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["ENDO_STAGE_I", "ENDO_STAGE_II", "ENDO_STAGE_III", "ENDO_STAGE_IV"].map((c, i) => (
            <button key={c} onClick={() => quickPick(c)}
              data-testid={`endo-quick-${c}`}
              className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
              Стадий {["I", "II", "III", "IV"][i]}
            </button>
          ))}
        </div>
      </div>

      {result && <SubtypeResultCard result={result} condition="endometriosis" API={API} onAssigned={onAssigned} />}
    </div>
  );
}

// ───────────────────────── IBD form ─────────────────────────
function IbdForm({ API, onAssigned }) {
  const [markers, setMarkers] = useState({
    location: null, granulomas: null, skip_lesions: null, histology_ambiguous: null,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const setM = (k, v) => setMarkers((m) => ({ ...m, [k]: v }));

  const classify = async () => {
    if (!markers.location) { toast.error("Изберете локация на възпалението."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/phenotype-subtype/classify`, { condition: "ibd", markers });
      setResult(data);
      if (!data.code) toast.warning("Недостатъчни данни за класификация.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при класификацията");
    } finally { setLoading(false); }
  };

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/ibd/${code}`);
      setResult({ condition: "ibd", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setLoading(false); }
  };

  const chip = (k, v, label) => {
    const active = markers[k] === v;
    return (
      <button key={`${k}-${String(v)}`} onClick={() => setM(k, active ? null : v)}
        data-testid={`ibd-${k}-${String(v)}`}
        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
          active ? "bg-sky-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"
        }`}>
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-3" data-testid="ibd-form">
      <p className="text-[11px] text-gray-500">
        Класификация по локация + хистология. Крон: трансмурално, грануломи,
        скок-образен pattern. УК: непрекъснато в колон/ректум.
      </p>

      <div className="space-y-2 text-[11px]">
        <div>
          <div className="font-semibold mb-1 text-gray-700 dark:text-gray-300">Локация</div>
          <div className="flex flex-wrap gap-1.5">
            {chip("location", "ileum", "Илеум (Крон)")}
            {chip("location", "colon_rectum", "Колон + ректум")}
            {chip("location", "colon", "Само колон")}
            {chip("location", "any_gi", "Всяка част от ГИТ")}
          </div>
        </div>
        <div>
          <div className="font-semibold mb-1 text-gray-700 dark:text-gray-300">Хистологични белези</div>
          <div className="flex flex-wrap gap-1.5">
            {chip("granulomas", true, "Грануломи (Крон)")}
            {chip("skip_lesions", true, "Скок-образни лезии")}
            {chip("histology_ambiguous", true, "Двусмислена хистология")}
          </div>
        </div>
      </div>

      <button onClick={classify} disabled={loading}
        className="w-full py-2 rounded-lg text-sm font-semibold bg-sky-500 hover:bg-sky-600 text-white disabled:opacity-40 transition-colors"
        data-testid="ibd-classify-btn">
        {loading ? "Класифициране..." : "Класифицирай IBD подтип"}
      </button>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
          Или директно изберете подтип:
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => quickPick("CROHN")} data-testid="ibd-quick-CROHN"
            className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">Крон</button>
          <button onClick={() => quickPick("ULCERATIVE_COLITIS")} data-testid="ibd-quick-UC"
            className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">Улцерозен колит</button>
          <button onClick={() => quickPick("IBD_U")} data-testid="ibd-quick-IBDU"
            className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">IBD-U</button>
        </div>
      </div>

      {result && <SubtypeResultCard result={result} condition="ibd" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── BREAST BENIGN (non-malignant) ─────────────────────────
function BreastBenignForm({ API, onAssigned }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/breast_benign/${code}`);
      setResult({ condition: "breast_benign", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setLoading(false); }
  };

  const btn = (code, label, testId, variant = "default") => {
    const variantClass = {
      default:    "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10",
      inflam:     "bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20",
      precancer:  "bg-rose-100 text-rose-800 hover:bg-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20",
      tumor:      "bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-500/10 dark:text-purple-300 dark:hover:bg-purple-500/20",
    }[variant];
    return (
      <button onClick={() => quickPick(code)} data-testid={`breast-benign-quick-${testId}`}
        className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${variantClass}`}>
        {label}
      </button>
    );
  };

  const sectionLabel = (text) => (
    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-2 mb-1">{text}</p>
  );

  return (
    <div className="space-y-3" data-testid="breast-benign-form">
      <p className="text-[11px] text-gray-500">
        Мамологични патологии — диагностика чрез мамография, ехография или биопсия. Изберете потвърденото състояние:
      </p>

      {/* Benign proliferative */}
      {sectionLabel("Доброкачествени")}
      <div className="flex flex-wrap gap-1.5">
        {btn("FIBROADENOMA", "Фиброаденом", "FIBROADENOMA")}
        {btn("FIBROCYSTIC", "Фиброкистична болест", "FIBROCYSTIC")}
        {btn("HIGH_DENSITY", "Висока плътност (BI-RADS C/D)", "HIGH_DENSITY")}
        {btn("JUV_PAPILLOMATOSIS", "Ювенилна папиломатоза", "JUV_PAPILLOMATOSIS")}
        {btn("BREAST_HAMARTOMA", "Хамартом", "BREAST_HAMARTOMA")}
        {btn("GALACTOCELE", "Галактоцеле", "GALACTOCELE")}
        {btn("SCLEROSING_ADENOSIS", "Склерозираща аденоза", "SCLEROSING_ADENOSIS")}
        {btn("INTRADUCTAL_PAPILLOMA", "Интрадуктален папилом", "INTRADUCTAL_PAPILLOMA")}
      </div>

      {/* Inflammatory / infectious */}
      {sectionLabel("Възпалителни / инфекциозни")}
      <div className="flex flex-wrap gap-1.5">
        {btn("MASTITIS", "Мастит", "MASTITIS", "inflam")}
        {btn("BREAST_ABSCESS", "Абсцес", "BREAST_ABSCESS", "inflam")}
      </div>

      {/* Pre-malignant (atypia + in situ) */}
      {sectionLabel("Pre-malignant / in situ")}
      <div className="flex flex-wrap gap-1.5">
        {btn("ADH", "Атипична дуктална хиперплазия (ADH)", "ADH", "precancer")}
        {btn("ALH", "Атипична лобуларна хиперплазия (ALH)", "ALH", "precancer")}
        {btn("DCIS", "Дуктален карцином in situ (DCIS)", "DCIS", "precancer")}
        {btn("LCIS", "Лобуларен карцином in situ (LCIS)", "LCIS", "precancer")}
      </div>

      {/* Fibroepithelial / mesenchymal tumours */}
      {sectionLabel("Фиброепителни / мезенхимни")}
      <div className="flex flex-wrap gap-1.5">
        {btn("MAMMARY_FIBROMATOSIS", "Фиброматоза (десмоид)", "MAMMARY_FIBROMATOSIS", "tumor")}
        {btn("PHYLLODES_TUMOR", "Филоиден тумор", "PHYLLODES_TUMOR", "tumor")}
      </div>

      {loading && <div className="text-[11px] text-gray-500 italic">Зареждане...</div>}
      {result && <SubtypeResultCard result={result} condition="breast_benign" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── GYNECOLOGY ─────────────────────────
function GynecologyForm({ API, onAssigned }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/gynecology/${code}`);
      setResult({ condition: "gynecology", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid="gynecology-form">
      <p className="text-[11px] text-gray-500">
        Гинекологични състояния — диагностика чрез ехография, MRI или биопсия.
      </p>
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => quickPick("UTERINE_FIBROIDS")} data-testid="gynecology-quick-UTERINE_FIBROIDS"
          className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
          Миома на матката (leiomyoma)
        </button>
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">Зареждане...</div>}
      {result && <SubtypeResultCard result={result} condition="gynecology" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── T2DM ─────────────────────────
function T2dmForm({ API, onAssigned }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/t2dm/${code}`);
      setResult({ condition: "t2dm", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`t2dm-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="t2dm-form">
      <p className="text-[11px] text-gray-500">
        Тип 2 диабет / преддиабет — фенотиптен подход. <b>Insulin resistance</b> e reversible предшественик (HbA1c 5.7-6.4); <b>MODY</b> изисква genetic testing;
        <b> LADA</b> — anti-GAD antibodies; <b>Classical</b> — insulin resistance + β-cell dysfunction.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {btn("INSULIN_RESISTANCE", "Инсулинова резистентност")}
        {btn("MODY", "MODY (genetic)")}
        {btn("LADA", "LADA (autoimmune)")}
        {btn("CLASSICAL", "Classical T2DM")}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">Зареждане...</div>}
      {result && <SubtypeResultCard result={result} condition="t2dm" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── Depression ─────────────────────────
function DepressionForm({ API, onAssigned }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/depression/${code}`);
      setResult({ condition: "depression", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`depression-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="depression-form">
      <p className="text-[11px] text-gray-500">
        Депресивни фенотипи по DSM-5 + research criteria. Диетата е АДЮНКТ към
        психиатричното лечение. При suicidal ideation → незабавна консултация.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {btn("ATYPICAL", "Атипична (hyperphagia + hypersomnia)")}
        {btn("MELANCHOLIC", "Меланхолична (anhedonia + HPA+)")}
        {btn("PSYCHOTIC", "Психотична")}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">Зареждане...</div>}
      {result && <SubtypeResultCard result={result} condition="depression" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── MAFLD ─────────────────────────
function MafldForm({ API, onAssigned }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/mafld/${code}`);
      setResult({ condition: "mafld", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`mafld-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="mafld-form">
      <p className="text-[11px] text-gray-500">
        Metabolic-Associated Fatty Liver Disease (MAFLD — renamed from NAFLD 2020).
        Изберете стадий според fibroscan/биопсия.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {btn("NAFL", "NAFL (simple steatosis F0-F1)")}
        {btn("NASH", "NASH (steatohepatitis F1-F3)")}
        {btn("CIRRHOSIS", "Cirrhosis (F4 end-stage)")}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">Зареждане...</div>}
      {result && <SubtypeResultCard result={result} condition="mafld" API={API} onAssigned={onAssigned} />}
    </div>
  );
}



// ───────────────────────── Multiple Sclerosis ─────────────────────────
function MsForm({ API, onAssigned }) {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const t = {
    bg: {
      desc: <>Мултиплена склероза — 4 клинични форми (Lublin 2014 criteria). Диетата е <b>АДЮНКТ</b> към neurologist-managed DMTs (Ocrevus, Tysabri и др.). Vitamin D + Swank диета имат най-силни доказателства.</>,
      rrms: "RRMS (relapsing-remitting, 85%)",
      spms: "SPMS (secondary progressive)",
      ppms: "PPMS (primary progressive, 10-15%)",
      prms: "PRMS (progressive-relapsing, rare)",
      loading: "Зареждане...",
      error: "Грешка",
    },
    en: {
      desc: <>Multiple sclerosis — 4 clinical forms (Lublin 2014 criteria). Diet is an <b>ADJUNCT</b> to neurologist-managed DMTs (Ocrevus, Tysabri, etc.). Vitamin D + Swank diet have the strongest evidence.</>,
      rrms: "RRMS (relapsing-remitting, 85%)",
      spms: "SPMS (secondary progressive)",
      ppms: "PPMS (primary progressive, 10-15%)",
      prms: "PRMS (progressive-relapsing, rare)",
      loading: "Loading...",
      error: "Error",
    },
    es: {
      desc: <>Esclerosis múltiple — 4 formas clínicas (criterios Lublin 2014). La dieta es un <b>ADYUVANTE</b> a los TME manejados por neurólogo (Ocrevus, Tysabri, etc.). Vitamina D + dieta Swank tienen la evidencia más fuerte.</>,
      rrms: "EMRR (remitente-recidivante, 85%)",
      spms: "EMSP (secundaria progresiva)",
      ppms: "EMPP (primariamente progresiva, 10-15%)",
      prms: "EMPR (progresiva-recidivante, rara)",
      loading: "Cargando...",
      error: "Error",
    },
  }[lang] || {};

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/ms/${code}`);
      setResult({ condition: "ms", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`ms-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="ms-form">
      <p className="text-[11px] text-gray-500">{t.desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {btn("RRMS", t.rrms)}
        {btn("SPMS", t.spms)}
        {btn("PPMS", t.ppms)}
        {btn("PRMS", t.prms)}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">{t.loading}</div>}
      {result && <SubtypeResultCard result={result} condition="ms" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── Migraine ─────────────────────────
function MigraineForm({ API, onAssigned }) {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const t = {
    bg: {
      desc: <>Мигрена по ICHD-3 критерии. <b>WITH_AURA</b> има ↑ риск от инсулт ×2 (жени &lt;45 + КОК + пушене → ×35). Превенцията с Mg + Riboflavin + CoQ10 имат <b>level A RCT доказателства</b>.</>,
      no_aura: "Без аура (70-90%)",
      aura: "С аура (10-30%, ↑ инсулт)",
      loading: "Зареждане...",
      error: "Грешка",
    },
    en: {
      desc: <>Migraine per ICHD-3 criteria. <b>WITH_AURA</b> has ↑ stroke risk ×2 (women &lt;45 + COCs + smoking → ×35). Prevention with Mg + Riboflavin + CoQ10 has <b>level A RCT evidence</b>.</>,
      no_aura: "Without aura (70-90%)",
      aura: "With aura (10-30%, ↑ stroke)",
      loading: "Loading...",
      error: "Error",
    },
    es: {
      desc: <>Migraña según criterios ICHD-3. <b>WITH_AURA</b> tiene riesgo de ictus ×2 (mujeres &lt;45 + ACO + fumar → ×35). La prevención con Mg + Riboflavina + CoQ10 tiene <b>evidencia nivel A</b>.</>,
      no_aura: "Sin aura (70-90%)",
      aura: "Con aura (10-30%, ↑ ictus)",
      loading: "Cargando...",
      error: "Error",
    },
  }[lang] || {};

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/migraine/${code}`);
      setResult({ condition: "migraine", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`migraine-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="migraine-form">
      <p className="text-[11px] text-gray-500">{t.desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {btn("WITHOUT_AURA", t.no_aura)}
        {btn("WITH_AURA", t.aura)}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">{t.loading}</div>}
      {result && <SubtypeResultCard result={result} condition="migraine" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── Rheumatoid Arthritis ─────────────────────────
function RaForm({ API, onAssigned }) {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const t = {
    bg: {
      desc: <>Ревматоиден артрит — серологичният статус определя прогнозата. <b>SEROPOSITIVE</b> (~70%) — HLA-DRB1 shared epitope, ерозивен; <b>SERONEGATIVE</b> (~30%) — припокриване със SpA спектър. Omega-3 3-4 g има meta-analysis доказателства (Goldberg 2007).</>,
      pos: "Серопозитивен (RF+/anti-CCP+)",
      neg: "Серонегативен (RF-/anti-CCP-)",
      loading: "Зареждане...",
      error: "Грешка",
    },
    en: {
      desc: <>Rheumatoid arthritis — serostatus determines prognosis. <b>SEROPOSITIVE</b> (~70%) — HLA-DRB1 shared epitope, erosive; <b>SERONEGATIVE</b> (~30%) — SpA spectrum overlap. Omega-3 3-4 g has meta-analysis evidence (Goldberg 2007).</>,
      pos: "Seropositive (RF+/anti-CCP+)",
      neg: "Seronegative (RF-/anti-CCP-)",
      loading: "Loading...",
      error: "Error",
    },
    es: {
      desc: <>Artritis reumatoide — el serostatus determina el pronóstico. <b>SEROPOSITIVE</b> (~70%) — HLA-DRB1, erosiva; <b>SERONEGATIVE</b> (~30%) — solapamiento con EspA. Omega-3 3-4 g tiene evidencia de meta-análisis (Goldberg 2007).</>,
      pos: "Seropositiva (FR+/anti-CCP+)",
      neg: "Seronegativa (FR-/anti-CCP-)",
      loading: "Cargando...",
      error: "Error",
    },
  }[lang] || {};

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/rheumatoid_arthritis/${code}`);
      setResult({ condition: "rheumatoid_arthritis", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`ra-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="ra-form">
      <p className="text-[11px] text-gray-500">{t.desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {btn("SEROPOSITIVE", t.pos)}
        {btn("SERONEGATIVE", t.neg)}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">{t.loading}</div>}
      {result && <SubtypeResultCard result={result} condition="rheumatoid_arthritis" API={API} onAssigned={onAssigned} />}
    </div>
  );
}



// ───────────────────────── Lung Cancer ─────────────────────────
// Споделен онкологичен disclaimer (всички 4 cancer форми го използват)
const ONCO_DISCLAIMER_I18N = {
  bg: {
    prefix: "⚠️ ОНКОЛОГИЧЕН ФЕНОТИП.",
    body: "Диетата е АДЮНКТ към oncology standard of care (хирургия, химиотерапия, имунотерапия, лъчетерапия). НЕ замества куративно лечение. Консултирайте се с онколог преди хранителни добавки — има drug-nutrient interactions.",
  },
  en: {
    prefix: "⚠️ ONCOLOGICAL PHENOTYPE.",
    body: "Diet is an ADJUNCT to oncology standard of care (surgery, chemotherapy, immunotherapy, radiation). It does NOT replace curative treatment. Consult with an oncologist before supplements — drug-nutrient interactions exist.",
  },
  es: {
    prefix: "⚠️ FENOTIPO ONCOLÓGICO.",
    body: "La dieta es un ADYUVANTE al tratamiento oncológico estándar (cirugía, quimioterapia, inmunoterapia, radioterapia). NO reemplaza el tratamiento curativo. Consulte con un oncólogo antes de tomar suplementos — existen interacciones fármaco-nutriente.",
  },
};

function OncoDisclaimer({ lang, extra }) {
  const t = ONCO_DISCLAIMER_I18N[lang] || ONCO_DISCLAIMER_I18N.bg;
  return (
    <div className="rounded-md p-2 border text-[10px] leading-relaxed flex items-start gap-1.5 border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200"
      data-testid="onco-disclaimer">
      <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
      <span>
        <b>{t.prefix}</b> {t.body} {extra && <span>{extra}</span>}
      </span>
    </div>
  );
}

function LungCancerForm({ API, onAssigned }) {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const t = {
    bg: {
      warning: "Критично: β-каротинови добавки УВЕЛИЧАВАТ риска ×1.18 при пушачи (CARET + ATBC trials).",
      desc: <>Белодробен карцином — СЗО 2021 класификация.</>,
      nsclc: "NSCLC (недребноклетъчен, ~85%)",
      sclc: "SCLC (дребноклетъчен, ~15%)",
      loading: "Зареждане...",
      error: "Грешка",
    },
    en: {
      warning: "Critical: β-carotene supplements INCREASE risk ×1.18 in smokers (CARET + ATBC trials).",
      desc: <>Lung cancer — WHO 2021 classification.</>,
      nsclc: "NSCLC (non-small cell, ~85%)",
      sclc: "SCLC (small-cell, ~15%)",
      loading: "Loading...",
      error: "Error",
    },
    es: {
      warning: "Crítico: suplementos de β-caroteno AUMENTAN el riesgo ×1.18 en fumadores (CARET + ATBC).",
      desc: <>Cáncer de pulmón — clasificación OMS 2021.</>,
      nsclc: "CPCNP (no microcítico, ~85%)",
      sclc: "CPCP (microcítico, ~15%)",
      loading: "Cargando...",
      error: "Error",
    },
  }[lang] || {};

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/lung_cancer/${code}`);
      setResult({ condition: "lung_cancer", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`lung-cancer-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="lung-cancer-form">
      <OncoDisclaimer lang={lang} extra={t.warning} />
      <p className="text-[11px] text-gray-500">{t.desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {btn("NSCLC", t.nsclc)}
        {btn("SCLC", t.sclc)}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">{t.loading}</div>}
      {result && <SubtypeResultCard result={result} condition="lung_cancer" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── Prostate Cancer ─────────────────────────
function ProstateCancerForm({ API, onAssigned }) {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const t = {
    bg: {
      warning: "SELECT trial: Vit E > 400 IU → риск за простатен карцином +17%.",
      desc: <>Простатен карцином — D'Amico risk stratification (Gleason score + PSA + T stage). <b>Нисък риск</b> → активно наблюдение; <b>висок</b> → мултимодална терапия.</>,
      low: "Нисък риск (Gleason ≤6)",
      inter: "Междинен (Gleason 7)",
      high: "Висок риск / метастатичен",
      loading: "Зареждане...",
      error: "Грешка",
    },
    en: {
      warning: "SELECT trial: Vit E > 400 IU → prostate CA risk +17%.",
      desc: <>Prostate cancer — D'Amico risk stratification (Gleason + PSA + T stage). <b>Low-risk</b> → active surveillance; <b>high</b> → multimodal therapy.</>,
      low: "Low-risk (Gleason ≤6)",
      inter: "Intermediate (Gleason 7)",
      high: "High-risk / metastatic",
      loading: "Loading...",
      error: "Error",
    },
    es: {
      warning: "Ensayo SELECT: Vit E > 400 UI → riesgo de cáncer de próstata +17%.",
      desc: <>Cáncer de próstata — estratificación D'Amico (Gleason + PSA + etapa T). <b>Bajo riesgo</b> → vigilancia activa; <b>alto</b> → terapia multimodal.</>,
      low: "Bajo riesgo (Gleason ≤6)",
      inter: "Intermedio (Gleason 7)",
      high: "Alto riesgo / metastásico",
      loading: "Cargando...",
      error: "Error",
    },
  }[lang] || {};

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/prostate_cancer/${code}`);
      setResult({ condition: "prostate_cancer", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`prostate-cancer-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="prostate-cancer-form">
      <OncoDisclaimer lang={lang} extra={t.warning} />
      <p className="text-[11px] text-gray-500">{t.desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {btn("LOW_RISK", t.low)}
        {btn("INTERMEDIATE_RISK", t.inter)}
        {btn("HIGH_RISK", t.high)}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">{t.loading}</div>}
      {result && <SubtypeResultCard result={result} condition="prostate_cancer" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── Colorectal Cancer ─────────────────────────
function ColorectalCancerForm({ API, onAssigned }) {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const t = {
    bg: {
      warning: "Преработено месо > 50 g/ден (IARC Group 1) + червено месо > 100 g/ден (IARC 2A) — абсолютно избягване.",
      desc: <>Колоректален карцином — Consensus Molecular Subtypes (CMS1-4, Guinney 2015 Nat Med). Определя се чрез molecular profiling (MSI тест + RNA sequencing).</>,
      cms1: "CMS1 (MSI-H/immune, ~14%)",
      cms2: "CMS2 (WNT/MYC, ~37%)",
      cms3: "CMS3 (metabolic/KRAS, ~13%)",
      cms4: "CMS4 (mesenchymal, ~23%)",
      loading: "Зареждане...",
      error: "Грешка",
    },
    en: {
      warning: "Processed meat > 50 g/day (IARC Group 1) + red meat > 100 g/day (IARC 2A) — absolute avoidance.",
      desc: <>Colorectal cancer — Consensus Molecular Subtypes (CMS1-4, Guinney 2015 Nat Med). Determined by molecular profiling (MSI testing + RNA sequencing).</>,
      cms1: "CMS1 (MSI-H/immune, ~14%)",
      cms2: "CMS2 (WNT/MYC, ~37%)",
      cms3: "CMS3 (metabolic/KRAS, ~13%)",
      cms4: "CMS4 (mesenchymal, ~23%)",
      loading: "Loading...",
      error: "Error",
    },
    es: {
      warning: "Carne procesada > 50 g/día (IARC Grupo 1) + carne roja > 100 g/día (IARC 2A) — evitar absolutamente.",
      desc: <>Cáncer colorrectal — Consensus Molecular Subtypes (CMS1-4, Guinney 2015). Se determina por perfil molecular (test MSI + secuenciación ARN).</>,
      cms1: "CMS1 (MSI-alto/inmune, ~14%)",
      cms2: "CMS2 (WNT/MYC, ~37%)",
      cms3: "CMS3 (metabólico/KRAS, ~13%)",
      cms4: "CMS4 (mesenquimal, ~23%)",
      loading: "Cargando...",
      error: "Error",
    },
  }[lang] || {};

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/colorectal_cancer/${code}`);
      setResult({ condition: "colorectal_cancer", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`colorectal-cancer-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="colorectal-cancer-form">
      <OncoDisclaimer lang={lang} extra={t.warning} />
      <p className="text-[11px] text-gray-500">{t.desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {btn("CMS1", t.cms1)}
        {btn("CMS2", t.cms2)}
        {btn("CMS3", t.cms3)}
        {btn("CMS4", t.cms4)}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">{t.loading}</div>}
      {result && <SubtypeResultCard result={result} condition="colorectal_cancer" API={API} onAssigned={onAssigned} />}
    </div>
  );
}


// ───────────────────────── Pancreatic Cancer ─────────────────────────
function PancreaticCancerForm({ API, onAssigned }) {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const t = {
    bg: {
      warning: "PDAC (95%) — агресивен, 5-годишна преживяемост < 10%. Pancreatic Enzyme Replacement (Креон) е ЗАДЪЛЖИТЕЛЕН — без ензими всяка диета е безсмислена.",
      desc: <>Панкреатичен карцином — PDAC (аденокарцином) vs PNET (невроендокринен). PNET има значително по-добра прогноза (5-год. 50-70% локализирани vs &lt; 10% PDAC).</>,
      pdac: "PDAC (ductal adeno, ~95%)",
      pnet: "PNET (невроендокринен, ~5%)",
      loading: "Зареждане...",
      error: "Грешка",
    },
    en: {
      warning: "PDAC (95%) — aggressive, 5-year survival < 10%. Pancreatic Enzyme Replacement (Creon) is MANDATORY — without enzymes any diet is futile.",
      desc: <>Pancreatic cancer — PDAC (adenocarcinoma) vs PNET (neuroendocrine). PNET has significantly better prognosis (5-yr 50-70% localized vs &lt; 10% PDAC).</>,
      pdac: "PDAC (ductal adeno, ~95%)",
      pnet: "PNET (neuroendocrine, ~5%)",
      loading: "Loading...",
      error: "Error",
    },
    es: {
      warning: "PDAC (95%) — agresivo, supervivencia a 5 años < 10%. La Terapia de Reemplazo Enzimático Pancreático (Creon) es OBLIGATORIA — sin enzimas cualquier dieta es inútil.",
      desc: <>Cáncer de páncreas — PDAC (adenocarcinoma) vs TNE (neuroendocrino). El TNE tiene pronóstico significativamente mejor (5 años: 50-70% localizados vs &lt; 10% PDAC).</>,
      pdac: "PDAC (adeno ductal, ~95%)",
      pnet: "TNE (neuroendocrino, ~5%)",
      loading: "Cargando...",
      error: "Error",
    },
  }[lang] || {};

  const quickPick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/pancreatic_cancer/${code}`);
      setResult({ condition: "pancreatic_cancer", code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally { setLoading(false); }
  };

  const btn = (code, label) => (
    <button onClick={() => quickPick(code)} data-testid={`pancreatic-cancer-quick-${code}`}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10">
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="pancreatic-cancer-form">
      <OncoDisclaimer lang={lang} extra={t.warning} />
      <p className="text-[11px] text-gray-500">{t.desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {btn("PDAC", t.pdac)}
        {btn("PNET", t.pnet)}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">{t.loading}</div>}
      {result && <SubtypeResultCard result={result} condition="pancreatic_cancer" API={API} onAssigned={onAssigned} />}
    </div>
  );
}




// ───────────── #4 нови фенотипа (April 2026) — generic quick-pick форми ─────────────
function _useTrLight() {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase() === "es" ? "es"
              : (language || "bg").toLowerCase() === "en" ? "en" : "bg";
  return {
    lang,
    loading: { bg: "Зареждане...", en: "Loading...", es: "Cargando..." }[lang],
    error: { bg: "Грешка", en: "Error", es: "Error" }[lang],
  };
}

function _GenericQuickPickForm({ condition, items, hint, API, onAssigned, dataTestPrefix }) {
  const { lang, loading: loadingTxt, error: errTxt } = _useTrLight();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const pick = async (code) => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/detail/${condition}/${code}`);
      setResult({ condition, code, subtype: data.subtype });
    } catch (e) {
      toast.error(e?.response?.data?.detail || errTxt);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid={`${dataTestPrefix}-form`}>
      {hint?.[lang] && <p className="text-[11px] text-gray-500">{hint[lang]}</p>}
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <button
            key={it.code}
            onClick={() => pick(it.code)}
            data-testid={`${dataTestPrefix}-quick-${it.code}`}
            className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"
          >
            {it.label[lang] || it.label.bg}
          </button>
        ))}
      </div>
      {loading && <div className="text-[11px] text-gray-500 italic">{loadingTxt}</div>}
      {result && <SubtypeResultCard result={result} condition={condition} API={API} onAssigned={onAssigned} />}
    </div>
  );
}

function CardiovascularForm({ API, onAssigned }) {
  return (
    <_GenericQuickPickForm
      condition="cardiovascular"
      hint={{
        bg: "Сърдечно-съдови — диагностика чрез лаборатории (LDL, ApoB, hsCRP), измерване на АН или Doppler ехография.",
        en: "Cardiovascular — diagnosed via labs (LDL, ApoB, hsCRP), BP measurement, or Doppler ultrasound.",
        es: "Cardiovascular — laboratorios, medición de PA o Doppler.",
      }}
      items={[
        { code: "ATHEROSCLEROSIS", label: { bg: "Атеросклероза", en: "Atherosclerosis", es: "Aterosclerosis" } },
        { code: "HYPERTENSION", label: { bg: "Хипертония", en: "Hypertension", es: "Hipertensión" } },
        { code: "DYSLIPIDEMIA", label: { bg: "Дислипидемия", en: "Dyslipidemia", es: "Dislipidemia" } },
        { code: "VARICOSE_VEINS", label: { bg: "Варикозни вени", en: "Varicose veins", es: "Várices" } },
      ]}
      dataTestPrefix="cardiovascular"
      API={API}
      onAssigned={onAssigned}
    />
  );
}

function RenalForm({ API, onAssigned }) {
  return (
    <_GenericQuickPickForm
      condition="renal"
      hint={{
        bg: "Бъбречни заболявания — диагностика чрез eGFR, UACR, серумен креатинин.",
        en: "Renal disease — eGFR, UACR, serum creatinine.",
        es: "Enfermedad renal — eGFR, UACR, creatinina.",
      }}
      items={[
        { code: "CKD", label: { bg: "ХБН (CKD)", en: "Chronic kidney disease", es: "ERC" } },
      ]}
      dataTestPrefix="renal"
      API={API}
      onAssigned={onAssigned}
    />
  );
}

function AdditionalOncologyForm({ API, onAssigned }) {
  return (
    <_GenericQuickPickForm
      condition="additional_oncology"
      hint={{
        bg: "Други онкологични — диагностика чрез биопсия + хистология. Записите подкрепят нутрицията като адюнкт към trimodal therapy.",
        en: "Other oncology — biopsy + histology. Records support nutrition adjunct to trimodal therapy.",
        es: "Otra oncología — biopsia + histología.",
      }}
      items={[
        { code: "GASTRIC_CANCER", label: { bg: "Стомашен карцином", en: "Gastric cancer", es: "Cáncer gástrico" } },
        { code: "INFLAMMATORY_BREAST_CANCER", label: { bg: "Инфламаторен рак на гърдата (IBC)", en: "Inflammatory breast cancer", es: "CMI" } },
      ]}
      dataTestPrefix="additional-oncology"
      API={API}
      onAssigned={onAssigned}
    />
  );
}

function EndocrineRareForm({ API, onAssigned }) {
  return (
    <_GenericQuickPickForm
      condition="endocrine_rare"
      hint={{
        bg: "Редки ендокринни — хормонален панел + кариотипиране (Turner/Klinefelter) или MRI на хипофизата.",
        en: "Rare endocrine — hormonal panel + karyotyping (Turner/Klinefelter) or pituitary MRI.",
        es: "Endocrino raro — panel hormonal + cariotipado o RMN.",
      }}
      items={[
        { code: "PITUITARY_ADENOMA", label: { bg: "Аденом на хипофизата", en: "Pituitary adenoma", es: "Adenoma hipofisario" } },
        { code: "TURNER_SYNDROME", label: { bg: "Синдром на Търнър", en: "Turner syndrome", es: "S. de Turner" } },
        { code: "KLINEFELTER_SYNDROME", label: { bg: "Синдром на Клайнфелтер", en: "Klinefelter syndrome", es: "S. de Klinefelter" } },
      ]}
      dataTestPrefix="endocrine-rare"
      API={API}
      onAssigned={onAssigned}
    />
  );
}

function GynecologyExtraForm({ API, onAssigned }) {
  return (
    <_GenericQuickPickForm
      condition="gynecology_extra"
      hint={{
        bg: "Гинекология (доп.) — ехография, MRI, биопсия или микробиология.",
        en: "Gynecology (extra) — ultrasound, MRI, biopsy or microbiology.",
        es: "Ginecología (extra) — ecografía, RMN, biopsia.",
      }}
      items={[
        { code: "UTERINE_POLYP", label: { bg: "Полип на матката", en: "Uterine polyp", es: "Pólipo uterino" } },
        { code: "UTERINE_ADENOMYOSIS", label: { bg: "Аденомиоза на матката", en: "Uterine adenomyosis", es: "Adenomiosis" } },
        { code: "IDIOPATHIC_GRANULOMATOUS_MASTITIS", label: { bg: "Идиопатичен грануломатозен мастит", en: "Idiopathic granulomatous mastitis", es: "MGI" } },
      ]}
      dataTestPrefix="gynecology-extra"
      API={API}
      onAssigned={onAssigned}
    />
  );
}

function DentalForm({ API, onAssigned }) {
  return (
    <_GenericQuickPickForm
      condition="dental"
      hint={{
        bg: "Дентални заболявания — стоматологичен преглед, panoramic X-ray.",
        en: "Dental diseases — dental exam, panoramic X-ray.",
        es: "Dentales — examen, RX panorámica.",
      }}
      items={[
        { code: "DENTAL_DISEASES", label: { bg: "Кариес и периодонтит", en: "Caries and periodontitis", es: "Caries y periodontitis" } },
      ]}
      dataTestPrefix="dental"
      API={API}
      onAssigned={onAssigned}
    />
  );
}


// ───────────────────────── Helper: Yes/No marker row ─────────────────────────
function MarkerRow({ label, sub, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <div className="flex-1 min-w-0">
        <div className="text-gray-700 dark:text-gray-300 truncate">{label}</div>
        {sub && <div className="text-[10px] text-gray-400 truncate">{sub}</div>}
      </div>
      <div className="flex gap-1.5 shrink-0">
        <button onClick={() => onChange(true)}
          className={`px-3 py-1 rounded-full text-[11px] transition-colors ${value === true ? "bg-emerald-500 text-white" : "bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300"}`}
          data-testid={`${testId}-yes`}>
          Да
        </button>
        <button onClick={() => onChange(false)}
          className={`px-3 py-1 rounded-full text-[11px] transition-colors ${value === false ? "bg-rose-500 text-white" : "bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300"}`}
          data-testid={`${testId}-no`}>
          Не
        </button>
      </div>
    </div>
  );
}

// ───────────────────── Deep Research View (lite + full) ─────────────────────
// Зарежда /phenotype-subtype/research/{condition}/{code} lazy когато е визуализиран.
// 2 режима: default "lite" (1-paragraph summary + top-3 nutrient links) и "full"
// (всички mutations, pathways, nutrient links + PMID citations).
const EVIDENCE_COLORS = {
  A: { bg: "bg-emerald-100 dark:bg-emerald-500/15", text: "text-emerald-700 dark:text-emerald-300", label: "A" },
  B: { bg: "bg-amber-100 dark:bg-amber-500/15", text: "text-amber-700 dark:text-amber-300", label: "B" },
  C: { bg: "bg-gray-100 dark:bg-white/10", text: "text-gray-600 dark:text-gray-300", label: "C" },
};

function PubmedLink({ text, isDark }) {
  // Ако изречението съдържа PMID XXXXXXXX или PMC... — прави го linkable.
  const pmidMatch = text.match(/PMID\s+(\d+)/i);
  const pmcMatch = text.match(/PMC(\d+)/i);
  const nctMatch = text.match(/(NCT\d+)/i);
  let href = null;
  if (pmidMatch) href = `https://pubmed.ncbi.nlm.nih.gov/${pmidMatch[1]}/`;
  else if (pmcMatch) href = `https://www.ncbi.nlm.nih.gov/pmc/articles/PMC${pmcMatch[1]}/`;
  else if (nctMatch) href = `https://clinicaltrials.gov/study/${nctMatch[1]}`;
  if (!href) return <span className={isDark ? "text-gray-400" : "text-gray-600"}>{text}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={`inline-flex items-center gap-0.5 underline decoration-dotted hover:no-underline ${isDark ? "text-sky-400 hover:text-sky-300" : "text-sky-600 hover:text-sky-700"}`}>
      {text}
      <ExternalLink className="w-2.5 h-2.5" />
    </a>
  );
}

function EvidenceBadge({ level }) {
  const cfg = EVIDENCE_COLORS[level] || EVIDENCE_COLORS.C;
  return (
    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-sm text-[9px] font-bold ${cfg.bg} ${cfg.text}`}
      title={level === "A" ? "Phase II+/RCT човешки данни" : level === "B" ? "Phase I / pilot / observational" : "Предклинични данни"}
      data-testid={`evidence-level-${level}`}>
      {cfg.label}
    </span>
  );
}

function NutrientLinkRow({ link, isDark, expanded }) {
  return (
    <div className={`rounded-md p-2 border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white"}`}
      data-testid={`nutrient-link-${link.id}`}>
      <div className="flex items-start gap-2">
        <EvidenceBadge level={link.evidence_level} />
        <div className="flex-1 min-w-0 space-y-1">
          <div className={`text-[11px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            {link.nutrient_bg}
          </div>
          {expanded && (
            <>
              <div className={`text-[10px] leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                <span className="font-semibold">Механизъм: </span>{link.mechanism_bg}
              </div>
              <div className={`text-[10px] ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                <span className="font-semibold">Доза: </span>{link.dose_human_equiv}
              </div>
              {link.targets_pathways?.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-0.5">
                  {link.targets_pathways.map((kegg) => (
                    <span key={kegg} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono ${isDark ? "bg-sky-500/10 text-sky-300" : "bg-sky-50 text-sky-700"}`}>
                      <a href={`https://www.kegg.jp/entry/${kegg}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {kegg}
                      </a>
                    </span>
                  ))}
                </div>
              )}
              <div className={`text-[10px] pt-1 space-y-0.5 border-t ${isDark ? "border-white/10" : "border-gray-100"}`}>
                {link.citations.map((c, i) => (
                  <div key={`${link.id}-cit-${i}-${String(c).slice(0, 20)}`} className="flex items-start gap-1">
                    <span className={isDark ? "text-gray-500" : "text-gray-400"}>•</span>
                    <PubmedLink text={c} isDark={isDark} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────── Evidence Rating Badge ─────────
// Показва A/B/C/D grade + score% + breakdown (брой RCT/cohort/mechanistic links)
function EvidenceRatingBadge({ rating, isDark }) {
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  if (!rating || rating.grade === "N/A") return null;
  const { grade, score_pct, breakdown, citation_count, label_bg, label_en, label_es, color } = rating;

  const label = { bg: label_bg, en: label_en || label_bg, es: label_es || label_en || label_bg }[lang];
  const title = { bg: "Оценка на доказателствата", en: "Evidence Rating", es: "Calificación de evidencia" }[lang];
  const refLbl = { bg: "реф.", en: "refs", es: "refs" }[lang];
  const totalLbl = { bg: "Общо", en: "Total", es: "Total" }[lang];

  return (
    <div
      className={`rounded-md border px-3 py-2 flex items-center gap-3
        ${isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}
      data-testid="evidence-rating-badge"
    >
      {/* Grade circle */}
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-base shrink-0"
        style={{ background: color }}
        data-testid={`evidence-grade-${grade}`}
        title={`Evidence Grade ${grade} · ${score_pct}%`}
      >
        {grade}
      </div>
      {/* Text + breakdown */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className={`text-[11px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            {title} · {score_pct}%
          </div>
          <div className={`text-[9px] font-mono ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            📚 {citation_count} {refLbl}
          </div>
        </div>
        <div className={`text-[10px] leading-tight mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {label}
        </div>
        {/* Breakdown bar */}
        <div className="flex gap-0.5 mt-1.5 h-1.5 rounded overflow-hidden">
          {breakdown.A > 0 && (
            <div style={{ background: "#16A34A", flex: breakdown.A }}
              title={`A (RCT/meta): ${breakdown.A}`} />
          )}
          {breakdown.B > 0 && (
            <div style={{ background: "#0EA5E9", flex: breakdown.B }}
              title={`B (cohort/pilot): ${breakdown.B}`} />
          )}
          {breakdown.C > 0 && (
            <div style={{ background: "#D97706", flex: breakdown.C }}
              title={`C (mechanistic): ${breakdown.C}`} />
          )}
        </div>
        <div className={`flex gap-3 mt-1 text-[9px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          <span>A·{breakdown.A}</span>
          <span>B·{breakdown.B}</span>
          <span>C·{breakdown.C}</span>
          <span>{totalLbl}: {breakdown.total_links}</span>
        </div>
      </div>
    </div>
  );
}



function DeepResearchView({ API, condition, code }) {
  const { isDark } = useTheme();
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [fullMode, setFullMode] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const { data } = await axios.get(`${API}/phenotype-subtype/research/${condition}/${code}`);
        if (!cancel) setResearch(data);
      } catch (e) {
        if (!cancel) {
          if (e?.response?.status === 404) setNotFound(true);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [API, condition, code]);

  if (loading) {
    return <div className={`text-[11px] italic ${isDark ? "text-gray-500" : "text-gray-400"}`}>Зареждам научен профил...</div>;
  }
  if (notFound) {
    return (
      <div className={`text-[11px] italic rounded p-2 ${isDark ? "text-gray-500 bg-white/5" : "text-gray-500 bg-gray-50"}`}
        data-testid="research-not-compiled">
        🧪 Дълбок научен профил все още не е compile-нат за този subtype. В процес на изграждане от редакцията.
      </div>
    );
  }
  if (!research) return null;

  const links = research.nutrient_pathway_links || [];
  // Sort by evidence level A→B→C
  const order = { A: 0, B: 1, C: 2 };
  const sorted = [...links].sort((a, b) => (order[a.evidence_level] ?? 9) - (order[b.evidence_level] ?? 9));
  const lite = sorted.slice(0, 3);
  const rendered = fullMode ? sorted : lite;

  return (
    <div className="space-y-2.5" data-testid="deep-research-view">
      {/* Evidence Rating badge — агрегиран от evidence_level на nutrient_pathway_links */}
      {research.evidence_rating && <EvidenceRatingBadge rating={research.evidence_rating} isDark={isDark} />}

      {/* Mandatory oncology disclaimer — ВИНАГИ показван */}
      <div className={`rounded-md p-2 border text-[10px] leading-relaxed flex items-start gap-1.5
        ${isDark ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-900"}`}
        data-testid="oncology-disclaimer">
        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
        <span>{research.disclaimer_bg}</span>
      </div>

      {/* Subtype-specific contraindications (напр. resveratrol в HER2+ LB) */}
      {(research.contraindications || []).map((c) => (
        <div key={c.id}
          className={`rounded-md p-2 border text-[10px] leading-relaxed flex items-start gap-1.5
            ${isDark ? "border-orange-500/40 bg-orange-500/10 text-orange-200" : "border-orange-300 bg-orange-50 text-orange-900"}`}
          data-testid={`contraindication-${c.id}`}>
          <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">{c.nutrient_bg}</div>
            <div>{c.warning_bg}</div>
            {c.citation && <div className={`mt-0.5 text-[9px] ${isDark ? "opacity-70" : "opacity-80"}`}>
              <PubmedLink text={c.citation} isDark={isDark} />
            </div>}
          </div>
        </div>
      ))}

      {/* Lite summary — винаги показан */}
      <div className={`text-[11px] leading-relaxed italic ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {research.lite_summary_bg}
      </div>

      {/* iter327L — Hydroxyproline / Col3 support protocol (Stewart 2024) */}
      {research.hydroxyproline_protocol && (
        <HydroxyprolineProtocolCard
          protocol={research.hydroxyproline_protocol}
          isDark={isDark}
        />
      )}

      {/* Nutrient–pathway links */}
      <div>
        <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1 flex items-center gap-2 ${isDark ? "text-sky-400" : "text-sky-700"}`}>
          <FlaskConical className="w-3 h-3" />
          <span>Научни интервенции ({sorted.length})</span>
          <span className={`ml-auto text-[9px] normal-case tracking-normal ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            A/B/C = evidence level
          </span>
        </div>
        <div className="space-y-1.5">
          {rendered.map((link) => (
            <NutrientLinkRow key={link.id} link={link} isDark={isDark} expanded={fullMode} />
          ))}
        </div>
        {sorted.length > 3 && (
          <button onClick={() => setFullMode((v) => !v)}
            className={`mt-2 inline-flex items-center gap-1 text-[10px] font-semibold transition-colors
              ${isDark ? "text-sky-400 hover:text-sky-300" : "text-sky-600 hover:text-sky-700"}`}
            data-testid="toggle-full-research">
            {fullMode ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {fullMode ? "Покажи lite изглед" : `Покажи пълно изследване (${sorted.length - 3} допълнителни)`}
          </button>
        )}
      </div>

      {/* Mutations + Pathways — само в full режим */}
      {fullMode && (
        <>
          <div>
            <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1 ${isDark ? "text-purple-400" : "text-purple-700"}`}>
              🧬 Ключови мутации (TCGA)
            </div>
            <div className="space-y-1">
              {research.key_mutations?.map((m) => (
                <div key={m.gene} className={`rounded-md p-1.5 text-[10px] ${isDark ? "bg-white/5" : "bg-gray-50"}`}
                  data-testid={`mutation-${m.gene}`}>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`font-mono font-bold ${isDark ? "text-purple-300" : "text-purple-700"}`}>{m.gene}</span>
                    <span className={isDark ? "text-gray-400" : "text-gray-600"}>{m.alteration}</span>
                    <span className={`text-[9px] px-1 rounded ${isDark ? "bg-purple-500/20 text-purple-200" : "bg-purple-100 text-purple-700"}`}>
                      {m.frequency}
                    </span>
                  </div>
                  <div className={`mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{m.role}</div>
                  <div className={`text-[9px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    <PubmedLink text={m.source} isDark={isDark} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1 ${isDark ? "text-cyan-400" : "text-cyan-700"}`}>
              🧪 Driver pathways (KEGG)
            </div>
            <div className="space-y-1">
              {research.driver_pathways?.map((p) => (
                <div key={p.kegg_id} className={`rounded-md p-1.5 text-[10px] ${isDark ? "bg-white/5" : "bg-gray-50"}`}
                  data-testid={`pathway-${p.kegg_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a href={`https://www.kegg.jp/entry/${p.kegg_id}`} target="_blank" rel="noopener noreferrer"
                      className={`font-mono font-bold inline-flex items-center gap-0.5 hover:underline ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>
                      {p.kegg_id}
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                    <span className={`font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}>{p.name_bg}</span>
                  </div>
                  <div className={`mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{p.summary_bg}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={`text-[9px] pt-1 border-t ${isDark ? "border-white/10 text-gray-500" : "border-gray-200 text-gray-500"}`}>
            <span className="font-semibold">Compiled from: </span>{research.compiled_from?.join(" · ")}
            {" · "}<span>Updated {research.last_updated}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ───────────────────────── Result card ─────────────────────────
// Помощник: избор на поле според текущия език (bg/en/es), с graceful fallback към BG
const pickLang = (obj, key, lang) => {
  if (!obj) return "";
  const suffixed = obj[`${key}_${lang}`];
  if (suffixed !== undefined && suffixed !== null && suffixed !== "") return suffixed;
  const bg = obj[`${key}_bg`];
  if (bg !== undefined && bg !== null && bg !== "") return bg;
  return obj[key] ?? "";
};

// Локализирани UI надписи (fallback за не-преведен dictionary)
const CONDITION_HEADER_LABEL = {
  bg: { breast_cancer: "PAM50 subtype", pcos: "Rotterdam phenotype", thyroid: "Тиреоиден фенотип", endometriosis: "ASRM стадий", ibd: "IBD подтип" },
  en: { breast_cancer: "PAM50 subtype", pcos: "Rotterdam phenotype", thyroid: "Thyroid phenotype", endometriosis: "ASRM stage", ibd: "IBD subtype" },
  es: { breast_cancer: "Subtipo PAM50", pcos: "Fenotipo de Rotterdam", thyroid: "Fenotipo tiroideo", endometriosis: "Estadio ASRM", ibd: "Subtipo EII" },
};

const UI_I18N = {
  bg: {
    assigning: "Записване...",
    save_to_profile: "Запиши в профила",
    saved_prefix: "Записано",
    save_error: "Грешка при запис",
    insufficient: "Недостатъчно данни за класификация.",
    insufficient_hint: "Попълнете задължителните маркери.",
    priority_indices: "Приоритетни индекси",
    emphasise: "✓ Акцентирайте върху",
    avoid: "✗ Избягвайте",
    source: "Източник:",
    pam50: "PAM50 subtype",
    rotterdam: "Rotterdam phenotype",
  },
  en: {
    assigning: "Saving...",
    save_to_profile: "Save to profile",
    saved_prefix: "Saved",
    save_error: "Save failed",
    insufficient: "Not enough data to classify.",
    insufficient_hint: "Please complete the required markers.",
    priority_indices: "Priority indices",
    emphasise: "✓ Emphasise",
    avoid: "✗ Avoid",
    source: "Source:",
    pam50: "PAM50 subtype",
    rotterdam: "Rotterdam phenotype",
  },
  es: {
    assigning: "Guardando...",
    save_to_profile: "Guardar en el perfil",
    saved_prefix: "Guardado",
    save_error: "Error al guardar",
    insufficient: "Datos insuficientes para clasificar.",
    insufficient_hint: "Complete los marcadores requeridos.",
    priority_indices: "Índices prioritarios",
    emphasise: "✓ Enfatizar",
    avoid: "✗ Evitar",
    source: "Fuente:",
    pam50: "Subtipo PAM50",
    rotterdam: "Fenotipo de Rotterdam",
  },
};
const uiT = (lang, key) => (UI_I18N[lang] || UI_I18N.bg)[key] || UI_I18N.bg[key];

function SubtypeResultCard({ result, condition, API, onAssigned }) {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [assigning, setAssigning] = useState(false);
  const { refreshUser } = useAuth();
  const targetUserId = useTargetUserId();

  if (!result?.subtype) {
    return (
      <div className={`rounded-lg p-3 text-[12px] border ${isDark ? "border-amber-500/30 bg-amber-500/5 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800"}`}
        data-testid="subtype-insufficient">
        {uiT(lang, "insufficient")} {result?.reason === "insufficient_markers" ? uiT(lang, "insufficient_hint") : ""}
      </div>
    );
  }

  const st = result.subtype;
  const stName = pickLang(st, "name", lang);
  const stExplanation = pickLang(st, "explanation", lang);
  const stEmphasise = pickLang(st, "emphasise", lang) || [];
  const stAvoid = pickLang(st, "avoid", lang) || [];

  const assign = async () => {
    setAssigning(true);
    try {
      await axios.post(`${API}/phenotype-subtype/assign`, withTargetBody({ condition, code: st.code }, targetUserId));
      toast.success(`${uiT(lang, "saved_prefix")}: ${stName}`);
      refreshUser?.();
      onAssigned?.(condition, st.code);
      // #8 — emit event so PhenotypeSynergyPanel auto-refreshes без reload
      try { window.dispatchEvent(new CustomEvent("nutramilo:phenotype-changed", { detail: { condition, code: st.code, action: "assign" } })); } catch { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || uiT(lang, "save_error"));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className={`rounded-lg p-3 space-y-3 border ${isDark ? "border-sky-500/20 bg-sky-500/5" : "border-sky-200 bg-sky-50/50"}`}
      data-testid={`subtype-result-${st.code}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-sky-400" : "text-sky-600"}`}>
            {(CONDITION_HEADER_LABEL[lang] || CONDITION_HEADER_LABEL.bg)[condition] || condition}
          </div>
          <div className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{stName}</div>
        </div>
        <button onClick={assign} disabled={assigning}
          className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50 transition-colors"
          data-testid="assign-subtype-btn">
          {assigning ? uiT(lang, "assigning") : uiT(lang, "save_to_profile")}
        </button>
      </div>

      <p className={`text-[11px] leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>{stExplanation}</p>

      {/* Priority indices */}
      {st.priority_indices?.length > 0 && (
        <div>
          <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {uiT(lang, "priority_indices")}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {st.priority_indices.map((k) => (
              <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono ${isDark ? "bg-white/10 text-sky-300" : "bg-sky-100 text-sky-700"}`}>
                {k.replace("_", "-")}
                <IndexInfoButton indexKey={k === "NUTRI_SCORE" ? "NUTRI_SCORE" : k} />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Emphasise list */}
      {stEmphasise?.length > 0 && (
        <div>
          <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1 ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
            {uiT(lang, "emphasise")}
          </div>
          <ul className="space-y-1">
            {stEmphasise.map((line, i) => (
              <li key={`em-${i}-${String(line).slice(0, 24)}`} className={`flex items-start gap-1.5 text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Avoid list */}
      {stAvoid?.length > 0 && (
        <div>
          <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1 ${isDark ? "text-rose-400" : "text-rose-700"}`}>
            {uiT(lang, "avoid")}
          </div>
          <ul className="space-y-1">
            {stAvoid.map((line, i) => (
              <li key={`av-${i}-${String(line).slice(0, 24)}`} className={`flex items-start gap-1.5 text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                <XCircle className="w-3 h-3 text-rose-500 shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={`text-[10px] pt-2 border-t ${isDark ? "border-white/10 text-gray-500" : "border-gray-200 text-gray-500"}`}>
        <span className="font-semibold">{uiT(lang, "source")} </span>{st.citation}
      </div>

      {/* iter398 — Дълбок научен профил (5-секционен expandable) */}
      {st.deep_science && <DeepScienceProfile profile={st.deep_science} lang={lang} isDark={isDark} />}

      {/* Deep Research View — научни pathway-и и PMID citations */}
      <div className={`pt-2 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
        <DeepResearchView API={API} condition={condition} code={st.code} />
      </div>
    </div>
  );
}

// iter398 — Deep scientific profile renderer (collapsible 5-section card).
function DeepScienceProfile({ profile, lang, isDark }) {
  const [open, setOpen] = useState(false);
  if (!profile) return null;
  const bg = lang === "bg";
  const pick = (key) => profile[`${key}_${lang}`] || profile[`${key}_bg`] || "";
  const sections = [
    { key: "pathophysiology", title_bg: "🧬 Патофизиология",         title_en: "🧬 Pathophysiology" },
    { key: "diagnostics",     title_bg: "🔬 Диагностика",             title_en: "🔬 Diagnostics" },
    { key: "standard_of_care",title_bg: "💊 Standard of care",         title_en: "💊 Standard of care" },
    { key: "diet_mechanism",  title_bg: "🥗 Молекулярен diet механизъм", title_en: "🥗 Molecular diet mechanism" },
    { key: "prognosis",       title_bg: "📊 Прогноза",                 title_en: "📊 Prognosis" },
  ];
  const studies = profile.key_studies || [];
  return (
    <div className={`pt-2 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="deep-science-toggle"
        className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-[11px] font-semibold transition-colors
          ${isDark ? "text-purple-300 hover:bg-purple-500/10" : "text-purple-700 hover:bg-purple-50"}`}
      >
        <span className="flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0-6 0z"/><path d="M12 14v7"/><path d="M5 8a7 7 0 1 1 14 0c0 4-3 6-3 9H8c0-3-3-5-3-9z"/></svg>
          {bg ? "Дълбок научен профил" : "Deep scientific profile"}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`transition-transform ${open ? "rotate-180" : ""}`}><path d="M6 9l6 6 6-6"/></svg>
      </button>
      {open && (
        <div className="mt-2 space-y-2.5" data-testid="deep-science-body">
          <div className={`text-[11px] font-bold italic ${isDark ? "text-purple-300" : "text-purple-700"}`}>
            {bg ? profile.title_bg : profile.title_en}
          </div>
          {sections.map((s) => {
            const text = pick(s.key);
            if (!text) return null;
            return (
              <div key={s.key} data-testid={`deep-science-section-${s.key}`}>
                <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {bg ? s.title_bg : s.title_en}
                </div>
                <div
                  className={`text-[11px] leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}
                  dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong style="color:inherit">$1</strong>') }}
                />
              </div>
            );
          })}
          {studies.length > 0 && (
            <div data-testid="deep-science-studies" className={`pt-2 border-t ${isDark ? "border-white/10" : "border-gray-100"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                📚 {bg ? "Ключови проучвания" : "Key studies"}
              </div>
              <ul className={`text-[10px] space-y-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {studies.map((s) => <li key={s}>• {s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── Assigned chip (shows current profile subtypes) ─────────────────────────
function AssignedSubtypesStrip({ API, refreshSignal, onChanged }) {
  const [my, setMy] = useState({});
  const { isDark } = useTheme();
  const targetUserId = useTargetUserId();

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(withTargetQuery(`${API}/phenotype-subtype/my`, targetUserId));
      setMy(data?.subtypes || {});
    } catch {
      setMy({});
    }
  }, [API, targetUserId]);

  useEffect(() => { fetch(); }, [fetch, refreshSignal]);

  const remove = async (cond) => {
    try {
      await axios.delete(withTargetQuery(`${API}/phenotype-subtype/my/${cond}`, targetUserId));
      toast.success("Фенотипът е премахнат от профила");
      fetch();
      onChanged?.();
      try { window.dispatchEvent(new CustomEvent("nutramilo:phenotype-changed", { detail: { condition: cond, action: "remove" } })); } catch { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    }
  };

  const entries = Object.entries(my);
  if (!entries.length) return null;

  return (
    <div className={`rounded-lg p-2.5 border ${isDark ? "border-emerald-500/20 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50"}`}
      data-testid="assigned-subtypes-strip">
      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
        Активни фенотипи в профила
      </div>
      <div className="flex flex-wrap gap-1.5">
        {entries.map(([cond, rec]) => (
          <span key={cond}
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-medium ${isDark ? "bg-emerald-500/20 text-emerald-200" : "bg-emerald-100 text-emerald-800"}`}
            data-testid={`assigned-${cond}`}>
            {rec.tag}
            <button onClick={() => remove(cond)} className="opacity-70 hover:opacity-100"
              title="Премахни"
              data-testid={`remove-${cond}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}



// ════════════════════════════════════════════════════════════════════
// UNIFIED HUBS (Apr 2026 reorg) — subtab navigation
// ════════════════════════════════════════════════════════════════════
function _HubShell({ subtabs, defaultSub, children, dataTestId }) {
  const [sub, setSub] = useState(defaultSub);
  return (
    <div className="space-y-3" data-testid={dataTestId}>
      <div className="flex flex-wrap gap-1">
        {subtabs.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            data-testid={`subtab-${s.id}`}
            className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors
              ${sub === s.id
                ? "bg-violet-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="pt-2">{typeof children === "function" ? children(sub) : children}</div>
    </div>
  );
}

function UnifiedOncologyHub({ API, onAssigned }) {
  return (
    <_HubShell
      dataTestId="unified-oncology"
      defaultSub="breast"
      subtabs={[
        { id: "breast", label: "Гърда (PAM50)" },
        { id: "lung", label: "Бял дроб" },
        { id: "prostate", label: "Простата" },
        { id: "colorectal", label: "Колоректален" },
        { id: "pancreatic", label: "Панкреас" },
        { id: "additional", label: "Стомах + IBC" },
      ]}
    >
      {(sub) => (
        <>
          {sub === "breast" && <BreastCancerForm API={API} onAssigned={onAssigned} />}
          {sub === "lung" && <LungCancerForm API={API} onAssigned={onAssigned} />}
          {sub === "prostate" && <ProstateCancerForm API={API} onAssigned={onAssigned} />}
          {sub === "colorectal" && <ColorectalCancerForm API={API} onAssigned={onAssigned} />}
          {sub === "pancreatic" && <PancreaticCancerForm API={API} onAssigned={onAssigned} />}
          {sub === "additional" && <AdditionalOncologyForm API={API} onAssigned={onAssigned} />}
        </>
      )}
    </_HubShell>
  );
}

function UnifiedMammologyHub({ API, onAssigned }) {
  return (
    <_HubShell
      dataTestId="unified-mammology"
      defaultSub="benign"
      subtabs={[
        { id: "benign", label: "Доброкачествени" },
        { id: "igm", label: "IGM (грануломатозен мастит)" },
      ]}
    >
      {(sub) => (
        <>
          {sub === "benign" && <BreastBenignForm API={API} onAssigned={onAssigned} />}
          {sub === "igm" && (
            <_GenericQuickPickForm
              condition="gynecology_extra"
              hint={{
                bg: "Идиопатичен грануломатозен мастит — рядко неонкологично грануломатозно възпаление.",
                en: "Idiopathic granulomatous mastitis — rare non-neoplastic granulomatous inflammation.",
                es: "MGI — inflamación granulomatosa no neoplásica.",
              }}
              items={[
                { code: "IDIOPATHIC_GRANULOMATOUS_MASTITIS", label: { bg: "IGM", en: "IGM", es: "MGI" } },
              ]}
              dataTestPrefix="mammology-igm"
              API={API}
              onAssigned={onAssigned}
            />
          )}
        </>
      )}
    </_HubShell>
  );
}

function UnifiedEndocrinologyHub({ API, onAssigned }) {
  return (
    <_HubShell
      dataTestId="unified-endocrinology"
      defaultSub="thyroid"
      subtabs={[
        { id: "thyroid", label: "Щитовидна жлеза" },
        { id: "rare", label: "Редки (хипофиза, Търнър, Клайнфелтер)" },
      ]}
    >
      {(sub) => (
        <>
          {sub === "thyroid" && <ThyroidForm API={API} onAssigned={onAssigned} />}
          {sub === "rare" && <EndocrineRareForm API={API} onAssigned={onAssigned} />}
        </>
      )}
    </_HubShell>
  );
}

function UnifiedGynecologyHub({ API, onAssigned }) {
  return (
    <_HubShell
      dataTestId="unified-gynecology"
      defaultSub="pcos"
      subtabs={[
        { id: "pcos", label: "PCOS" },
        { id: "endometriosis", label: "Ендометриоза" },
        { id: "fibroids", label: "Миома" },
        { id: "polyp", label: "Полип на матката" },
        { id: "adenomyosis", label: "Аденомиоза" },
      ]}
    >
      {(sub) => (
        <>
          {sub === "pcos" && <PcosForm API={API} onAssigned={onAssigned} />}
          {sub === "endometriosis" && <EndometriosisForm API={API} onAssigned={onAssigned} />}
          {sub === "fibroids" && <GynecologyForm API={API} onAssigned={onAssigned} />}
          {sub === "polyp" && (
            <_GenericQuickPickForm
              condition="gynecology_extra"
              hint={{ bg: "Полип на матката — диагностика чрез ехография/хистероскопия.", en: "Uterine polyp — diagnosis via ultrasound/hysteroscopy.", es: "Pólipo uterino." }}
              items={[{ code: "UTERINE_POLYP", label: { bg: "Полип на матката", en: "Uterine polyp", es: "Pólipo uterino" } }]}
              dataTestPrefix="gyn-polyp"
              API={API}
              onAssigned={onAssigned}
            />
          )}
          {sub === "adenomyosis" && (
            <_GenericQuickPickForm
              condition="gynecology_extra"
              hint={{ bg: "Аденомиоза — junctional zone >12 mm на T2 MRI.", en: "Adenomyosis — junctional zone >12 mm on T2 MRI.", es: "Adenomiosis — zona unión >12 mm." }}
              items={[{ code: "UTERINE_ADENOMYOSIS", label: { bg: "Аденомиоза на матката", en: "Uterine adenomyosis", es: "Adenomiosis uterina" } }]}
              dataTestPrefix="gyn-adenomyosis"
              API={API}
              onAssigned={onAssigned}
            />
          )}
        </>
      )}
    </_HubShell>
  );
}

// ───────────────────────── Main panel ─────────────────────────
export default function PhenotypeClassifier(props) {
  return (
    <ErrorBoundary label="Phenotype Classifier">
      <PhenotypeClassifierInner {...props} />
    </ErrorBoundary>
  );
}

function PhenotypeClassifierInner({ targetUserId = null }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const lang = (language || "bg").toLowerCase();
  const [tab, setTab] = useState("breast_cancer");
  const [refreshSignal, setRefreshSignal] = useState(0);

  const onAssigned = () => setRefreshSignal((x) => x + 1);

  const contextValue = useMemo(() => ({ targetUserId }), [targetUserId]);

  const panelSubtitle = { bg: "PAM50 · Rotterdam · Тиреоидни · Ендометриоза · IBD · Бенигнена гърда · Гинекология · T2DM · Депресия · MAFLD",
                          en: "PAM50 · Rotterdam · Thyroid · Endometriosis · IBD · Benign breast · Gynecology · T2DM · Depression · MAFLD",
                          es: "PAM50 · Rotterdam · Tiroides · Endometriosis · EII · Mama benigna · Ginecología · DM2 · Depresión · MAFLD" }[lang] || "";
  const panelTitle = { bg: "Анализи", en: "Analyses", es: "Análisis" }[lang];
  const disclaimerText = { bg: "Класификацията е образователна — не замества клинична диагноза. Винаги обсъждайте резултатите с лекуващия си лекар.",
                           en: "The classification is educational — it does not replace a clinical diagnosis. Always discuss results with your physician.",
                           es: "La clasificación es educativa — no sustituye un diagnóstico clínico. Consulte siempre los resultados con su médico." }[lang];

  return (
    <PhenotypeTargetContext.Provider value={contextValue}>
    <CollapsiblePanel
      title={panelTitle}
      icon={Dna}
      iconColor="#EC4899"
      credits={0}
      subtitle={panelSubtitle}
      defaultOpen={false}
      storageKey="phenotype-classifier-open"
      testId="phenotype-classifier"
    >
      <div className="space-y-3">
        <AssignedSubtypesStrip API={API} refreshSignal={refreshSignal} onChanged={onAssigned} />

        {/* Tab switcher */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {CONDITIONS.map((c) => {
            const Icon = c.icon;
            const active = tab === c.id;
            const label = c[`label_${lang}`] || c.label_bg;
            return (
              <button key={c.id} onClick={() => setTab(c.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                  active
                    ? "bg-pink-500 text-white shadow-sm"
                    : isDark ? "bg-white/5 text-gray-400 hover:text-gray-200" : "bg-gray-100 text-gray-600 hover:text-gray-800"
                }`}
                data-testid={`phenotype-tab-${c.id}`}>
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>

        {/* ─── ОНКОЛОГИЯ — unified картинома хъб ─── */}
        {tab === "oncology" && <UnifiedOncologyHub API={API} onAssigned={onAssigned} />}
        {/* ─── МАМОЛОГИЯ — обединява breast cancer + breast benign + IGM ─── */}
        {tab === "mammology" && <UnifiedMammologyHub API={API} onAssigned={onAssigned} />}
        {/* ─── ЕНДОКРИНОЛОГИЯ — щитовидна + редки ендокринни ─── */}
        {tab === "endocrinology" && <UnifiedEndocrinologyHub API={API} onAssigned={onAssigned} />}
        {/* ─── ГИНЕКОЛОГИЯ — обединява старата + extra (PCOS, ендомет., аденомиоза, миома, полип) ─── */}
        {tab === "gynecology" && <UnifiedGynecologyHub API={API} onAssigned={onAssigned} />}

        {/* legacy individual hub-able forms — deprecated, kept за direct deep-links */}
        {tab === "pcos" && <PcosForm API={API} onAssigned={onAssigned} />}
        {tab === "endometriosis" && <EndometriosisForm API={API} onAssigned={onAssigned} />}
        {tab === "breast_cancer" && <BreastCancerForm API={API} onAssigned={onAssigned} />}
        {tab === "breast_benign" && <BreastBenignForm API={API} onAssigned={onAssigned} />}
        {tab === "thyroid" && <ThyroidForm API={API} onAssigned={onAssigned} />}
        {tab === "ibd" && <IbdForm API={API} onAssigned={onAssigned} />}
        {tab === "t2dm" && <T2dmForm API={API} onAssigned={onAssigned} />}
        {tab === "depression" && <DepressionForm API={API} onAssigned={onAssigned} />}
        {tab === "mafld" && <MafldForm API={API} onAssigned={onAssigned} />}
        {tab === "ms" && <MsForm API={API} onAssigned={onAssigned} />}
        {tab === "migraine" && <MigraineForm API={API} onAssigned={onAssigned} />}
        {tab === "rheumatoid_arthritis" && <RaForm API={API} onAssigned={onAssigned} />}
        {tab === "lung_cancer" && <LungCancerForm API={API} onAssigned={onAssigned} />}
        {tab === "prostate_cancer" && <ProstateCancerForm API={API} onAssigned={onAssigned} />}
        {tab === "colorectal_cancer" && <ColorectalCancerForm API={API} onAssigned={onAssigned} />}
        {tab === "pancreatic_cancer" && <PancreaticCancerForm API={API} onAssigned={onAssigned} />}
        {tab === "cardiovascular" && <CardiovascularForm API={API} onAssigned={onAssigned} />}
        {tab === "renal" && <RenalForm API={API} onAssigned={onAssigned} />}
        {tab === "additional_oncology" && <AdditionalOncologyForm API={API} onAssigned={onAssigned} />}
        {tab === "endocrine_rare" && <EndocrineRareForm API={API} onAssigned={onAssigned} />}
        {tab === "gynecology_extra" && <GynecologyExtraForm API={API} onAssigned={onAssigned} />}
        {tab === "dental" && <DentalForm API={API} onAssigned={onAssigned} />}



        <div className={`text-[10px] italic pt-2 border-t ${isDark ? "border-white/10 text-gray-500" : "border-gray-100 text-gray-500"}`}>
          {disclaimerText}
        </div>
      </div>
    </CollapsiblePanel>
    </PhenotypeTargetContext.Provider>
  );
}
